import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useSupportAuth } from "../context/SupportAuthContext";
import SupportLogin from "../support/Login";
import WaitingApproval from "../support/WaitingApproval";
import SupportDashboard from "../support/Dashboard";
import SupportAccessDenied from "../support/AccessDenied";
import AddLead from "../pages/lead/AddLead";
import LeadReport from "../pages/lead/LeadReport";
import LeadReport11 from "../pages/lead/LeadReport11";
import FollowupTable from "../components/features/dashboards/dashboardl1/dashboard/FollowupTable";
import LeadSource from "../components/features/dashboards/dashboardl1/dashboard/LeadSource";
import PotentialStatus from "../components/features/dashboards/dashboardl1/dashboard/potentialStatus";
import LeadConversionRate from "../components/features/dashboards/dashboardl1/dashboard/LeadConversionRate";
import ProbabilityStatus from "../components/features/dashboards/dashboardl1/dashboard/ProbabilityStatus";
import HistoricalData from "../components/features/dashboards/dashboardl1/dashboard/HistoricalData";
import RevenueDistribution from "../components/features/dashboards/dashboardl1/dashboard/RevenueDistribution";
import SalesProjects from "../pages/Operations/SalesOperations/SaleOperation/SalesParent/SalesProjects";
import TaskInHand from "../pages/Operations/OperationFeatures/TaskInHand";
import UtilizationReport from "../pages/Operations/OperationFeatures/UtilizationReport";
import AddGroup from "../pages/settings/AddGroup";
import Status from "../pages/settings/Status";
import Source from "../pages/settings/Source";
import LeadType from "../pages/settings/LeadType";
import Inventory from "../pages/settings/Inventory";
import ClientRatingDashboardL3 from "../components/features/dashboards/dashboardl3/clientrating/ClientRatingDashboardL3";
import Assembly from "../pages/settings/Assembly";
import Subparts from "../pages/settings/Subparts";
import HourlyRates from "../pages/settings/HourlyRates";
import GandATax from "../pages/settings/GandATax";
import Tax from "../pages/settings/Tax";
import SalesFinaceFilter from "../pages/Operations/SalesOperations/SalesFinance/TableSalesFinance/SalesFinaceFilter";
import SupportLayout from "../support/SupportLayout";

const SupportProtectedRoute = ({ moduleName, children }) => {
  const { token, hasModuleAccess } = useSupportAuth();

  if (!token) {
    return <Navigate to="/support/login" replace />;
  }

  if (moduleName && !hasModuleAccess(moduleName)) {
    return <Navigate to="/support/access-denied" replace />;
  }

  return children;
};

const SupportModuleRoute = ({ moduleName, element }) => (
  <SupportProtectedRoute moduleName={moduleName}>{element}</SupportProtectedRoute>
);

// Keeps standalone dashboard widgets (rendered alone in the support layout)
// at the same width they have inside the regular dashboards, so charts and
// cards don't visually stretch across the whole page.
const SupportWidgetWrapper = ({ widthClass = "w-1/2", children }) => (
  <div className={`${widthClass} flex h-full min-h-0 min-w-0`}>{children}</div>
);

const SupportRoutes = () => {
  return (
    <Routes>
      <Route path="login" element={<SupportLogin />} />
      <Route path="waiting" element={<WaitingApproval />} />
      <Route path="access-denied" element={<SupportAccessDenied />} />
      <Route
        path="dashboard"
        element={
          <SupportProtectedRoute>
            <SupportLayout />
          </SupportProtectedRoute>
        }
      >
        <Route
          index
          element={
            <SupportProtectedRoute>
              <SupportDashboard />
            </SupportProtectedRoute>
          }
        />
        <Route
          path="add-lead"
          element={<SupportModuleRoute moduleName="addlead" element={<AddLead />} />}
        />
        <Route
          path="edit-lead/:id"
          element={<SupportModuleRoute moduleName="addlead" element={<AddLead />} />}
        />
        <Route
          path="lead-report"
          element={<SupportModuleRoute moduleName="leadreport" element={<LeadReport />} />}
        />
        <Route
          path="leadreport"
          element={<SupportModuleRoute moduleName="leadreport" element={<LeadReport />} />}
        />
        <Route
          path="lead-report11"
          element={<SupportModuleRoute moduleName="leadreport" element={<LeadReport11 />} />}
        />
        <Route
          path="follow-up-table"
          element={<SupportModuleRoute moduleName="followuptable" element={<FollowupTable />} />}
        />
        <Route
          path="lead-source"
          element={
            <SupportModuleRoute
              moduleName="leadsource"
              element={
                <SupportWidgetWrapper widthClass="w-1/2">
                  <LeadSource />
                </SupportWidgetWrapper>
              }
            />
          }
        />
        <Route
          path="potential-status"
          element={
            <SupportModuleRoute
              moduleName="potentialstatus"
              element={
                <SupportWidgetWrapper widthClass="w-1/2">
                  <PotentialStatus />
                </SupportWidgetWrapper>
              }
            />
          }
        />
        <Route
          path="lead-conversion-rate"
          element={
            <SupportModuleRoute
              moduleName="leadconversionrate"
              element={
                <SupportWidgetWrapper widthClass="w-1/2">
                  <LeadConversionRate />
                </SupportWidgetWrapper>
              }
            />
          }
        />
        <Route
          path="probability-status"
          element={
            <SupportModuleRoute
              moduleName="probabilitystatus"
              element={
                <SupportWidgetWrapper widthClass="w-1/2">
                  <ProbabilityStatus />
                </SupportWidgetWrapper>
              }
            />
          }
        />
        <Route
          path="historical-data"
          element={
            <SupportModuleRoute
              moduleName="historicaldata"
              element={
                <SupportWidgetWrapper widthClass="w-1/2">
                  <HistoricalData />
                </SupportWidgetWrapper>
              }
            />
          }
        />
        <Route
          path="revenue-distribution"
          element={
            <SupportModuleRoute
              moduleName="revenuedistribution"
              element={
                <SupportWidgetWrapper widthClass="w-1/2">
                  <RevenueDistribution />
                </SupportWidgetWrapper>
              }
            />
          }
        />
        <Route
          path="sales-projects"
          element={<SupportModuleRoute moduleName="salesprojects" element={<SalesProjects />} />}
        />
        <Route
          path="task-in-hand"
          element={
            <SupportModuleRoute
              moduleName="taskinhand"
              element={
                <SupportWidgetWrapper widthClass="w-[25%]">
                  <TaskInHand />
                </SupportWidgetWrapper>
              }
            />
          }
        />
        <Route
          path="utilization-report"
          element={
            <SupportModuleRoute
              moduleName="utilizationreport"
              element={
                <SupportWidgetWrapper widthClass="w-[35%]">
                  <UtilizationReport />
                </SupportWidgetWrapper>
              }
            />
          }
        />
        <Route
          path="add-group"
          element={<SupportModuleRoute moduleName="addgroup" element={<AddGroup />} />}
        />
        <Route
          path="status"
          element={<SupportModuleRoute moduleName="status" element={<Status />} />}
        />
        <Route
          path="source"
          element={<SupportModuleRoute moduleName="source" element={<Source />} />}
        />
        <Route
          path="lead-type"
          element={<SupportModuleRoute moduleName="leadtype" element={<LeadType />} />}
        />
        <Route
          path="inventory"
          element={<SupportModuleRoute moduleName="inventory" element={<Inventory />} />}
        />
        <Route
          path="client-ratings"
          element={<SupportModuleRoute moduleName="clientratingdashboardl3" element={<ClientRatingDashboardL3 />} />}
        />
        <Route
          path="assembly"
          element={<SupportModuleRoute moduleName="assembly" element={<Assembly />} />}
        />
        <Route
          path="subparts"
          element={<SupportModuleRoute moduleName="subparts" element={<Subparts />} />}
        />
        <Route
          path="hourly-rates"
          element={<SupportModuleRoute moduleName="hourlyrates" element={<HourlyRates />} />}
        />
        <Route
          path="g-a-tax"
          element={<SupportModuleRoute moduleName="gandatax" element={<GandATax />} />}
        />
        <Route
          path="tax"
          element={<SupportModuleRoute moduleName="tax" element={<Tax />} />}
        />
        <Route
          path="sales-finance-filter"
          element={<SupportModuleRoute moduleName="salesfinancefilter" element={<SalesFinaceFilter />} />}
        />
      </Route>
      {/* Backward-compatible redirects to new support dashboard namespace */}
      <Route path="add-lead" element={<Navigate to="/support/dashboard/add-lead" replace />} />
      <Route path="lead-report" element={<Navigate to="/support/dashboard/lead-report" replace />} />
      <Route path="leadreport" element={<Navigate to="/support/dashboard/leadreport" replace />} />
      <Route path="lead-report11" element={<Navigate to="/support/dashboard/lead-report11" replace />} />
      <Route path="*" element={<Navigate to="/support/login" replace />} />
    </Routes>
  );
};

export default SupportRoutes;

